import React, { useEffect, useState } from 'react';
import {
  Table, Button, Tag, Typography, Space, Tooltip, Popconfirm, message, Empty, Card, Modal, Input,
} from 'antd';
import {
  PlusOutlined, FormOutlined, EditOutlined, DeleteOutlined, CodeOutlined, LinkOutlined, CopyOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;

const formUrl  = (slug) => `${window.location.origin}/f/${slug}`;
const embedCode = (slug) =>
  `<iframe src="${formUrl(slug)}" width="100%" height="520" style="border:0;max-width:480px" frameborder="0" title="Signup form"></iframe>`;

export default function Forms() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [embed, setEmbed] = useState(null); // form being embedded

  const load = async () => {
    setLoading(true);
    try { setRows((await axios.get('/api/forms')).data || []); }
    catch (e) { if (!(e.response?.status === 403)) message.error('Failed to load forms'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (row) => {
    try { await axios.delete(`/api/forms/${row.id}`); message.success('Form deleted'); load(); }
    catch (e) { message.error(e.response?.data?.message || 'Delete failed'); }
  };

  const copy = (text, label) => { navigator.clipboard.writeText(text); message.success(`${label} copied`); };

  const columns = [
    {
      title: 'Form', key: 'name', width: 240, ellipsis: true,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ cursor: 'pointer' }} onClick={() => navigate(`/forms/${r.id}/edit`)}>{r.name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>→ {r.list_name || `List #${r.list_id}`}</Text>
        </Space>
      ),
    },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: s => <Tag color={s === 'active' ? 'success' : 'default'}>{s === 'active' ? 'Active' : 'Paused'}</Tag> },
    { title: 'Opt-in', dataIndex: 'double_opt_in', key: 'optin', width: 110,
      render: d => <Tag color={d ? 'blue' : 'default'}>{d ? 'Double' : 'Single'}</Tag> },
    { title: 'Signups', dataIndex: 'submissions', key: 'subs', width: 90, render: n => n || 0 },
    {
      title: '', key: 'actions', width: 160, align: 'right',
      render: (_, r) => (
        <Space size={2}>
          <Tooltip title="Get embed code"><Button type="text" size="small" icon={<CodeOutlined />} onClick={() => setEmbed(r)} /></Tooltip>
          <Tooltip title="Edit"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => navigate(`/forms/${r.id}/edit`)} /></Tooltip>
          <Popconfirm title="Delete this form?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => remove(r)}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}><FormOutlined /> Signup Forms</Title>
          <Text type="secondary">Embeddable opt-in forms that grow your lists automatically.</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/forms/create')}>New Form</Button>
      </div>

      <Card bodyStyle={{ padding: 0 }} style={{ borderRadius: 14 }}>
        <Table
          rowKey="id" dataSource={rows} columns={columns} loading={loading}
          pagination={{ pageSize: 15, hideOnSinglePage: true }} scroll={{ x: 'max-content' }}
          locale={{ emptyText: <Empty image={<FormOutlined style={{ fontSize: 40, color: 'var(--text-3)' }} />} description="No forms yet. Create one to start capturing subscribers." /> }}
        />
      </Card>

      <Modal title={<Space><CodeOutlined />Embed your form</Space>} open={!!embed} onCancel={() => setEmbed(null)} footer={null} width={560}>
        {embed && (
          <>
            <Text type="secondary" style={{ fontSize: 13 }}>Hosted link — share anywhere</Text>
            <Input
              readOnly value={formUrl(embed.slug)} style={{ marginTop: 4, marginBottom: 16 }}
              addonAfter={<Space><a href={formUrl(embed.slug)} target="_blank" rel="noreferrer"><LinkOutlined /></a><CopyOutlined onClick={() => copy(formUrl(embed.slug), 'Link')} style={{ cursor: 'pointer' }} /></Space>}
            />
            <Text type="secondary" style={{ fontSize: 13 }}>Embed code — paste into your website's HTML</Text>
            <Input.TextArea readOnly value={embedCode(embed.slug)} rows={3} style={{ marginTop: 4, fontFamily: 'monospace', fontSize: 12 }} />
            <Button icon={<CopyOutlined />} onClick={() => copy(embedCode(embed.slug), 'Embed code')} style={{ marginTop: 10 }}>Copy embed code</Button>
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
              The form runs on a secure SES Mailbox page inside the iframe — no setup needed on your site.
            </Paragraph>
          </>
        )}
      </Modal>
    </div>
  );
}
